import React, { useState, useEffect } from 'react';
import {
  FileSearch,
  AlertTriangle,
  Globe,
  CheckCircle2,
  TrendingUp,
  Clock,
  ArrowUpRight,
  Building2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import PHI3ReviewQueue from '../components/PHI3ReviewQueue';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function Dashboard() {
  const [stats, setStats] = useState(null);
  const [recentTenders, setRecentTenders] = useState([]);
  const [recentRuns, setRecentRuns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, tendersRes, runsRes] = await Promise.all([
          fetch(`${API_URL}/api/dashboard/stats`),
          fetch(`${API_URL}/api/tenders?limit=5`),
          fetch(`${API_URL}/api/runs?limit=5`)
        ]);

        const statsData = await statsRes.json();
        const tendersData = await tendersRes.json();
        const runsData = await runsRes.json();

        setStats(statsData);
        setRecentTenders(tendersData.tenders || []);
        setRecentRuns(runsData || []);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <div className="loading-spinner" />;
  }

  const getPriorityClass = (priority) => {
    switch (priority?.toUpperCase()) {
      case 'HIGH': return 'priority-high';
      case 'MEDIUM': return 'priority-medium';
      case 'LOW': return 'priority-low';
      default: return 'priority-low';
    }
  };

  const getCompanyClass = (company) => {
    switch (company?.toUpperCase()) {
      case 'ALEPHTAV': return 'company-alephtav';
      case 'MAU_TECH': return 'company-mautech';
      default: return 'company-irrelevant';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div data-testid="dashboard-page">
      {/* Stats Grid */}
      <div className="stats-grid">
        <div className="stat-card" data-testid="stat-total-tenders">
          <div className="stat-icon blue">
            <FileSearch size={22} />
          </div>
          <div className="stat-value">{stats?.total_tenders || 0}</div>
          <div className="stat-label">Total Tenders</div>
        </div>

        <div className="stat-card" data-testid="stat-high-priority">
          <div className="stat-icon red">
            <AlertTriangle size={22} />
          </div>
          <div className="stat-value">{stats?.high_priority || 0}</div>
          <div className="stat-label">High Priority</div>
        </div>

        <div className="stat-card" data-testid="stat-active-sites">
          <div className="stat-icon green">
            <Globe size={22} />
          </div>
          <div className="stat-value">{stats?.active_sites || 0}/{stats?.total_sites || 0}</div>
          <div className="stat-label">Active Sites</div>
        </div>

        <div className="stat-card" data-testid="stat-today-tenders">
          <div className="stat-icon orange">
            <TrendingUp size={22} />
          </div>
          <div className="stat-value">{stats?.today_tenders || 0}</div>
          <div className="stat-label">Today's Tenders</div>
        </div>
      </div>

      {/* Company Distribution */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', marginBottom: 24 }}>
        <div className="stat-card">
          <div className="stat-icon blue">
            <Building2 size={22} />
          </div>
          <div className="stat-value">{stats?.company_distribution?.alephtav || 0}</div>
          <div className="stat-label">Alephtav Tenders</div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'hsl(270 60% 95%)', color: 'hsl(270 60% 35%)' }}>
            <Building2 size={22} />
          </div>
          <div className="stat-value">{stats?.company_distribution?.mautech || 0}</div>
          <div className="stat-label">Mau Tech Tenders</div>
        </div>
      </div>

      {/* Two Column Layout */}
      <div className="two-column-grid">
        {/* Recent Tenders */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recent Tenders</span>
              <a href="/tenders" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                View all <ArrowUpRight size={14} />
              </a>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentTenders.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <FileSearch size={28} />
                </div>
                <div className="empty-state-title">No tenders yet</div>
                <div className="empty-state-text">
                  Click "Run Now" to start scraping tenders from configured sites.
                </div>
              </div>
            ) : (
              <div>
                {recentTenders.map((tender) => (
                  <div key={tender._id} className="recent-tender-item" data-testid={`tender-row-${tender._id}`}>
                    <div style={{ flex: 1 }}>
                      <div className="tender-title">
                        {tender.title?.substring(0, 80)}...
                      </div>
                      <div className="tender-meta">
                        <span className={`company-badge-inline ${getCompanyClass(tender.company)}`}>
                          {tender.company?.replace('_', ' ') || 'Unknown'}
                        </span>
                        <span style={{ marginLeft: 8 }}>{tender.site_name || 'Unknown source'}</span>
                      </div>
                    </div>
                    <div className={`priority-badge ${getPriorityClass(tender.priority)}`}>
                      <span className="priority-dot" />
                      {tender.priority || 'Low'}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* PHI-3 AI Review Queue */}
        <PHI3ReviewQueue />
      </div>

      {/* Recent Runs - Full Width */}
      <div style={{ marginTop: 24 }}>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Recent Runs</span>
              <a href="/history" className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                View all <ArrowUpRight size={14} />
              </a>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {recentRuns.length === 0 ? (
              <div className="empty-state">
                <div className="empty-state-icon">
                  <Clock size={28} />
                </div>
                <div className="empty-state-title">No runs yet</div>
                <div className="empty-state-text">
                  Runs will appear here after you trigger a tender scout.
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {recentRuns.map((run) => (
                  <div key={run._id} className="recent-tender-item" data-testid={`run-row-${run._id}`}>
                    <div>
                      <div className="tender-title" style={{ fontSize: 14 }}>
                        {formatDate(run.started_at)}
                      </div>
                      <div className="tender-meta">
                        {run.sites_processed || 0} sites • {run.tenders_found || 0} tenders
                      </div>
                    </div>
                    <Badge
                      variant="secondary"
                      className={`status-${run.status?.toLowerCase()}`}
                      data-testid="run-status-badge"
                    >
                      {run.status === 'completed' && <CheckCircle2 size={12} />}
                      {run.status}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Dashboard;
